import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  InfoBody,
  InfoButton,
  InfoCard,
  InfoLegend,
  InfoLegendRow,
  InfoMeaning,
  InfoScrim,
  InfoTitle,
  StatePill,
} from '../styled-comps';
import { INFO } from '../constants';
import { TONE } from '../utils';

const CARD_WIDTH = 324;
const GAP = 8;
const EDGE = 10;

/**
 * The small "i" beside a tile title, and the panel it opens.
 *
 * The board's vocabulary is not self-evident — REPLY OWED, PARKED and
 * ABANDONED AT BOT all mean something specific — and a supervisor reading a
 * wall display will never go and find a doc. Each legend row renders as the
 * very pill it describes, so the explanation and the thing explained cannot
 * drift apart.
 */
const InfoTip = ({ topic }) => {
  const entry = INFO[topic];
  const [anchor, setAnchor] = useState(null);
  const cardRef = useRef(null);
  const [top, setTop] = useState(0);

  const open = useCallback(e => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    setAnchor({
      left: Math.max(EDGE, Math.min(r.left, window.innerWidth - CARD_WIDTH - EDGE)),
      top: r.top,
      bottom: r.bottom,
    });
  }, []);

  const close = useCallback(() => setAnchor(null), []);

  // Prefer below the "i"; flip above when it would run off the bottom; clamp
  // if it fits neither way. Measured after paint so the height is real.
  useLayoutEffect(() => {
    if (!anchor || !cardRef.current) return;
    const h = cardRef.current.offsetHeight;
    const vh = window.innerHeight;
    if (anchor.bottom + GAP + h <= vh - EDGE) setTop(anchor.bottom + GAP);
    else if (anchor.top - GAP - h >= EDGE) setTop(anchor.top - GAP - h);
    else setTop(Math.max(EDGE, vh - EDGE - h));
  }, [anchor]);

  useEffect(() => {
    if (!anchor) return undefined;
    const onKey = e => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [anchor, close]);

  if (!entry) return null;

  return (
    <>
      <InfoButton
        role="button"
        tabIndex={0}
        onClick={open}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') open(e);
        }}
        title={`About ${entry.title.toLowerCase()}`}
      >
        i
      </InfoButton>
      {anchor && (
        // The scrim covers the viewport but renders inside the tile, which
        // IS a <button> — so without stopPropagation every dismissing click
        // also fired the tile underneath, opening a modal or jumping a pane.
        <InfoScrim
          onClick={e => {
            e.stopPropagation();
            close();
          }}
        >
          <InfoCard
            ref={cardRef}
            onClick={e => e.stopPropagation()}
            $left={anchor.left}
            $top={top}
            $maxHeight={Math.max(160, window.innerHeight - EDGE * 2)}
          >
            <InfoTitle>{entry.title}</InfoTitle>
            <InfoBody>{entry.body}</InfoBody>
            <InfoLegend>
              {entry.legend.map(([tone, label, meaning]) => (
                <InfoLegendRow key={`${tone}-${label}`}>
                  <StatePill $tone={TONE[tone] || TONE.well}>{label}</StatePill>
                  <InfoMeaning>{meaning}</InfoMeaning>
                </InfoLegendRow>
              ))}
            </InfoLegend>
          </InfoCard>
        </InfoScrim>
      )}
    </>
  );
};

export default InfoTip;
